import { useEffect } from "react";
import { Film, Play, Sparkles } from "lucide-react";
import SiteHeader from "../components/site/SiteHeader";
import SiteFooter from "../components/site/SiteFooter";
import FloatingDonate from "../components/site/FloatingDonate";
import { useT } from "../i18n/LanguageContext";
import {
  SHERIFA_SCREENING,
  SHERIFA_MOTHER_DAUGHTER,
  SHERIFA_CHILDREN,
  SHERIFA_SAVE_LIVES,
  SHERIFA_YOUTUBE_ID,
  SHERIFA_PREMIERE_PHOTOS,
} from "../components/site/siteAssets";

const copy = {
  en: {
    kicker: "SHERIFA · Behind the Scenes",
    title: "SHERIFA Film.",
    sub: "A glimpse into the filmmaking journey and the story behind the production.",
    intro:
      "This gallery features photographs and videos captured during the production of the film. Included are behind-the-scenes moments, cast interactions, and scenes from the movie set, offering a glimpse into the filmmaking journey and the story behind the production.",
    videoKicker: "Official trailer",
    videoTitle: "Watch the film.",
    premiereKicker: "Premiere · A milestone evening",
    premiereTitle: "SHERIFA Premiere Event.",
    premiereBody1: "The premiere of Sherifa marked a significant milestone in the film's journey. These photographs showcase red-carpet arrivals, cast and crew appearances, audience engagement, and special moments from the evening's celebration.",
    premiereBody2: "The event highlighted the film's mission to inspire dialogue and raise awareness about challenges facing women and girls, while celebrating the dedication of everyone involved in bringing the story of Sherifa to life.",
    premiereMore: "More premiere photos coming soon.",
    galleryKicker: "From the production",
    galleryTitle: "Behind the scenes.",
    outreachKicker: "Community outreach · Togo, 2014",
    outreachHead: "11 villages. One conversation.",
    outreachIntro: "The photographs below were taken during a community outreach tour across 11 villages in Togo in 2014, one year after the premiere of the film Sherifa in Togo.",
    outreachBody1: "Following each screening of the film, my team and I facilitated discussions and workshops with community members on important social issues, including female genital mutilation (FGM), human trafficking of young girls, domestic violence against women and children, sexual assault, and the importance of education for girls.",
    outreachBody2: "These conversations provided an opportunity for villagers to share their experiences, ask questions, and engage in meaningful dialogue aimed at raising awareness and promoting positive change within their communities.",
    outreachVillagesLabel: "The 11 villages where we screened SHERIFA and held workshops",
    outreachVillages: [
      "Agouloudé", "Kpassouadé", "Aguidagbade", "Sabarignadé",
      "Koussountou", "Yélivo", "Aléhéridé", "Koumoniadé",
      "Kadambara", "Kparataou", "Tchalo",
    ],
    outreachClosing: "Through these screenings and community discussions, we sought to empower individuals, promote education, and encourage collective action to protect the rights and well-being of women and children.",
    captions: {
      screening: "Outdoor community screening of SHERIFA in Togo — audiences gathered after sundown to watch the film together.",
      motherDaughter: "Cast moment — a mother and daughter from the SHERIFA story.",
      children: "Children of the village featured in the SHERIFA production.",
      saveLives: "On set with the \"Save Lives / Sauver des Vies\" campaign team during the SHERIFA production.",
    },
  },
  fr: {
    kicker: "SHERIFA · Coulisses",
    title: "Le Film SHERIFA.",
    sub: "Un aperçu du parcours du tournage et de l'histoire derrière la production.",
    intro:
      "Cette galerie présente des photographies et des vidéos capturées pendant le tournage du film. On y trouve des moments des coulisses, des interactions entre les acteurs et des scènes du plateau, offrant un aperçu du processus de réalisation et de l'histoire derrière la production.",
    videoKicker: "Bande-annonce officielle",
    videoTitle: "Regarder le film.",
    premiereKicker: "Première · Une soirée marquante",
    premiereTitle: "Première du film SHERIFA.",
    premiereBody1: "La première de Sherifa a marqué une étape majeure du parcours du film. Ces photographies présentent les arrivées sur tapis rouge, la présence du casting et de l'équipe, l'engagement du public et les moments forts de la soirée.",
    premiereBody2: "L'événement a mis en lumière la mission du film : susciter le dialogue et sensibiliser aux défis auxquels font face les femmes et les filles, tout en célébrant l'engagement de toutes celles et ceux qui ont donné vie à l'histoire de Sherifa.",
    premiereMore: "D'autres photos de la première seront ajoutées prochainement.",
    galleryKicker: "Pendant le tournage",
    galleryTitle: "Dans les coulisses.",
    outreachKicker: "Sensibilisation communautaire · Togo, 2014",
    outreachHead: "11 villages. Une seule conversation.",
    outreachIntro: "Les photographies ci-dessous ont été prises lors d'une tournée de sensibilisation communautaire dans 11 villages du Togo en 2014, un an après la première du film Sherifa au Togo.",
    outreachBody1: "Après chaque projection du film, mon équipe et moi animions des discussions et des ateliers avec les membres de la communauté sur des enjeux sociaux majeurs : les mutilations génitales féminines (MGF), la traite des jeunes filles, les violences domestiques contre les femmes et les enfants, les agressions sexuelles, et l'importance de la scolarisation des filles.",
    outreachBody2: "Ces conversations donnaient l'occasion aux villageois·es de partager leurs expériences, de poser des questions et d'engager un dialogue constructif visant à sensibiliser et à promouvoir un changement positif au sein de leur communauté.",
    outreachVillagesLabel: "Les 11 villages où nous avons projeté SHERIFA et animé les ateliers",
    outreachVillages: [
      "Agouloudé", "Kpassouadé", "Aguidagbade", "Sabarignadé",
      "Koussountou", "Yélivo", "Aléhéridé", "Koumoniadé",
      "Kadambara", "Kparataou", "Tchalo",
    ],
    outreachClosing: "À travers ces projections et ces échanges communautaires, nous avons cherché à autonomiser les personnes, à promouvoir l'éducation et à encourager une action collective pour protéger les droits et le bien-être des femmes et des enfants.",
    captions: {
      screening: "Projection en plein air du film SHERIFA au Togo — la communauté s'est rassemblée après le coucher du soleil pour regarder le film ensemble.",
      motherDaughter: "Moment de tournage — une mère et sa fille dans l'histoire de SHERIFA.",
      children: "Les enfants du village figurant dans la production SHERIFA.",
      saveLives: "Sur le plateau avec l'équipe de la campagne « Save Lives / Sauver des Vies » lors du tournage de SHERIFA.",
    },
  },
};

export default function SherifaFilm() {
  const t = useT(copy);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F4EDE0] text-[#2A1F15]" style={{ fontFamily: "'Figtree','Work Sans',sans-serif" }} data-testid="sherifa-film-page">
      <SiteHeader />

      {/* Hero */}
      <section className="relative bg-[#1A0F0A] text-[#F4EDE0] overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-25">
          <img src={SHERIFA_SCREENING} alt="" className="w-full h-full object-cover" />
        </div>
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-[#1A0F0A]/40 via-[#1A0F0A]/70 to-[#1A0F0A]" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">
            <Film size={14} /> {t.kicker}
          </div>
          <h1
            className="mt-6 text-5xl md:text-7xl leading-[1.05]"
            style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}
            data-testid="sherifa-film-title"
          >
            {t.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#F4EDE0]/85 leading-relaxed" style={{ fontFamily: "'Merriweather',serif" }}>
            {t.sub}
          </p>
        </div>
      </section>

      {/* Intro paragraph */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[820px] mx-auto px-6 md:px-10">
          <p
            className="text-lg md:text-xl leading-[1.85] text-[#2A1F15]/90"
            style={{ fontFamily: "'Merriweather',serif" }}
            data-testid="sherifa-film-intro"
          >
            {t.intro}
          </p>
        </div>
      </section>

      {/* YouTube embed */}
      <section className="py-16 md:py-24 bg-[#F4EDE0]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold">
            <Play size={14} /> {t.videoKicker}
          </div>
          <h2
            className="mt-4 text-4xl md:text-5xl leading-[1.05]"
            style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}
            data-testid="sherifa-video-title"
          >
            {t.videoTitle}
          </h2>
          <div className="mt-8 relative w-full aspect-video rounded-xl overflow-hidden border-4 border-[#2A1F15] shadow-2xl bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${SHERIFA_YOUTUBE_ID}?si=Pkql0M10SQwVcZGN`}
              title="SHERIFA Film — YouTube video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="sherifa-video-iframe"
            />
          </div>
        </div>
      </section>

      {/* SHERIFA Premiere Event */}
      <section className="py-20 md:py-28 bg-[#1A0F0A] text-[#F4EDE0] relative overflow-hidden">
        <div aria-hidden className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(circle, #E3282B 0%, transparent 60%)" }} />
        <div aria-hidden className="absolute -bottom-32 -left-32 h-[360px] w-[360px] rounded-full blur-3xl opacity-20" style={{ background: "radial-gradient(circle, #D4A93B 0%, transparent 60%)" }} />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold" data-testid="sherifa-premiere-kicker">
            <Sparkles size={14} /> {t.premiereKicker}
          </div>
          <h2
            className="mt-4 text-4xl md:text-5xl leading-[1.05]"
            style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}
            data-testid="sherifa-premiere-title"
          >
            {t.premiereTitle}
          </h2>

          <div className="mt-8 space-y-5 text-base md:text-lg leading-[1.85] text-[#F4EDE0]/90 max-w-3xl" style={{ fontFamily: "'Merriweather',serif" }}>
            <p>{t.premiereBody1}</p>
            <p>{t.premiereBody2}</p>
          </div>

          {/* Premiere photo grid — flexible, easy to expand */}
          <div className="mt-14 grid grid-cols-12 gap-5" data-testid="sherifa-premiere-grid">
            {SHERIFA_PREMIERE_PHOTOS.map((url, i) => {
              const borders = ["border-[#D4A93B]", "border-[#E3282B]", "border-[#F4EDE0]", "border-[#00A651]", "border-[#8C5A3C]"];
              // Tasteful asymmetric layout: first photo wide (cast lineup), the next two as portraits beside it
              const layout =
                i === 0
                  ? "col-span-12 md:col-span-8 aspect-[16/10]"
                  : i === 1
                    ? "col-span-6 md:col-span-4 aspect-[3/4]"
                    : i === 2
                      ? "col-span-6 md:col-span-4 aspect-[3/4]"
                      : "col-span-6 md:col-span-3 aspect-square";
              return (
                <figure
                  key={i}
                  data-testid={`sherifa-premiere-photo-${i}`}
                  className={`${layout} overflow-hidden rounded-xl border-4 ${borders[i % borders.length]} shadow-2xl`}
                >
                  <img src={url} alt="SHERIFA Premiere Event" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </figure>
              );
            })}
          </div>

          <p className="mt-8 text-sm italic text-[#F4EDE0]/65 max-w-2xl">{t.premiereMore}</p>
        </div>
      </section>

      {/* 2014 Community outreach tour — 11 villages */}
      <section className="py-20 md:py-28 bg-[#F4EDE0]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold" data-testid="sherifa-outreach-kicker">{t.outreachKicker}</div>
          <h2
            className="mt-4 text-4xl md:text-5xl leading-[1.05]"
            style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}
            data-testid="sherifa-outreach-head"
          >
            {t.outreachHead}
          </h2>

          <div className="mt-8 space-y-6 text-base md:text-lg leading-[1.85] text-[#2A1F15]/90 max-w-3xl" style={{ fontFamily: "'Merriweather',serif" }} data-testid="sherifa-outreach-body">
            <p className="text-lg md:text-xl font-semibold text-[#2A1F15]">{t.outreachIntro}</p>
            <p>{t.outreachBody1}</p>
            <p>{t.outreachBody2}</p>
          </div>

          <div className="mt-12 rounded-xl bg-white border-l-4 border-[#D4A93B] shadow-md p-7 md:p-9">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#8C5A3C] font-bold" data-testid="sherifa-outreach-villages-label">
              {t.outreachVillagesLabel}
            </div>
            <ul className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3" data-testid="sherifa-outreach-villages">
              {t.outreachVillages.map((v, i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-3 text-base md:text-lg text-[#2A1F15]"
                  style={{ fontFamily: "'Merriweather',serif" }}
                >
                  <span className="text-[#E3282B] font-bold text-sm tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>

          <p
            className="mt-10 max-w-3xl text-base md:text-lg leading-[1.85] italic text-[#8C5A3C]"
            style={{ fontFamily: "'Merriweather',serif" }}
            data-testid="sherifa-outreach-closing"
          >
            {t.outreachClosing}
          </p>
        </div>
      </section>

      {/* Behind-the-scenes gallery */}
      <section className="py-20 md:py-28 bg-white border-y-4 border-[#D4A93B]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold">{t.galleryKicker}</div>
          <h2
            className="mt-4 text-4xl md:text-5xl leading-[1.05]"
            style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}
            data-testid="sherifa-gallery-title"
          >
            {t.galleryTitle}
          </h2>

          <div className="mt-12 grid grid-cols-12 gap-6" data-testid="sherifa-gallery-grid">
            {/* Outdoor screening — large, hero card */}
            <figure className="col-span-12 md:col-span-8">
              <div className="aspect-[16/10] overflow-hidden rounded-xl border-4 border-[#2A1F15] shadow-2xl bg-black">
                <img src={SHERIFA_SCREENING} alt={t.captions.screening} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <figcaption className="mt-3 text-sm italic text-[#2A1F15]/70 leading-relaxed">{t.captions.screening}</figcaption>
            </figure>

            {/* Mother + daughter portrait — tall side card */}
            <figure className="col-span-12 md:col-span-4">
              <div className="aspect-[3/4] overflow-hidden rounded-xl border-4 border-[#E3282B] shadow-xl">
                <img src={SHERIFA_MOTHER_DAUGHTER} alt={t.captions.motherDaughter} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <figcaption className="mt-3 text-sm italic text-[#2A1F15]/70 leading-relaxed">{t.captions.motherDaughter}</figcaption>
            </figure>

            {/* Village children — wide */}
            <figure className="col-span-12 md:col-span-7">
              <div className="aspect-[4/3] overflow-hidden rounded-xl border-4 border-[#00A651] shadow-xl">
                <img src={SHERIFA_CHILDREN} alt={t.captions.children} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <figcaption className="mt-3 text-sm italic text-[#2A1F15]/70 leading-relaxed">{t.captions.children}</figcaption>
            </figure>

            {/* Save Lives team */}
            <figure className="col-span-12 md:col-span-5">
              <div className="aspect-[4/3] overflow-hidden rounded-xl border-4 border-[#D4A93B] shadow-xl">
                <img src={SHERIFA_SAVE_LIVES} alt={t.captions.saveLives} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <figcaption className="mt-3 text-sm italic text-[#2A1F15]/70 leading-relaxed">{t.captions.saveLives}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <SiteFooter />
      <FloatingDonate />
    </div>
  );
}
